'use client';

import { useState, useTransition, useId } from 'react';
import { home } from '@/lib/content';

type Variant = 'light' | 'dark';

export function NewsletterForm({
  source = 'home-newsletter',
  variant = 'dark',
}: { source?: string; variant?: Variant }) {
  const [email, setEmail]     = useState('');
  // Unchecked by default: consent must be an explicit act, not a pre-ticked box.
  const [optIn, setOptIn]     = useState(false);
  const [hp, setHp]           = useState('');
  const [pending, startTransition] = useTransition();
  const [status, setStatus]   = useState<'idle' | 'success' | 'error'>('idle');
  const [errorText, setErrorText] = useState('');
  const checkboxId = useId();
  const emailId    = useId();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.includes('@') || !optIn) {
      setStatus('error');
      return;
    }
    startTransition(async () => {
      try {
        const res = await fetch('/api/newsletter', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, source, website: hp }),
        });
        const json = await res.json();
        if (json.ok) {
          setStatus('success');
          setEmail('');
        } else {
          setErrorText(typeof json.error === 'string' ? json.error : home.newsletter.errorMsg);
          setStatus('error');
        }
      } catch (err) {
        console.error('[newsletter] submit failed', err);
        setErrorText(home.newsletter.errorMsg);
        setStatus('error');
      }
    });
  }

  const isLight = variant === 'light';
  const inputClass = isLight
    ? 'flex-1 bg-transparent border-b border-ink/20 px-1 py-3 text-sm text-ink placeholder:text-muted-strong focus:border-ink focus:outline-none transition-colors'
    : 'flex-1 bg-transparent border border-bone/20 sm:border-r-0 px-4 py-3 text-sm text-bone placeholder:text-bone/40 focus:border-bone focus:outline-none transition-colors';
  const buttonClass = isLight
    ? 'bg-plum text-white px-10 py-3.5 text-xs uppercase tracking-[0.12em] font-medium hover:bg-plum-deep transition-colors disabled:opacity-60 self-start rounded-sm'
    : 'bg-bone text-ink px-8 py-3 text-xs uppercase tracking-[0.12em] font-medium hover:bg-bone/90 transition-colors disabled:opacity-60';
  const successClass = isLight
    ? 'text-ink py-4 text-xs uppercase tracking-[0.12em] font-medium'
    : 'text-bone text-center py-4 text-xs uppercase tracking-[0.12em] font-medium';
  const errorClass = isLight
    ? 'text-ink/60 text-xs mt-3'
    : 'text-bone/60 text-xs absolute mt-14';
  const checkboxLabelClass = isLight ? 'text-ink/70' : 'text-bone/70';
  const checkboxClass = isLight
    ? 'size-4 accent-ink mr-3 cursor-pointer'
    : 'size-4 accent-bone mr-3 cursor-pointer';

  if (status === 'success') {
    return (
      <p className={successClass}>
        {home.newsletter.successMsg}
      </p>
    );
  }

  if (isLight) {
    return (
      <form onSubmit={handleSubmit} className="max-w-md flex flex-col gap-5 relative">
      <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
        <label>Leave this field empty<input type="text" name="website" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} /></label>
      </div>
        <div>
          <label htmlFor={emailId} className="text-xs uppercase tracking-[0.12em] text-muted-strong font-medium mb-2 block">Email *</label>
          <input
            id={emailId}
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setStatus('idle'); }}
            placeholder=""
            required
            className={inputClass}
            aria-label="Email address for newsletter signup"
          />
        </div>
        <label className="flex items-center text-xs cursor-pointer">
          <input
            id={checkboxId}
            type="checkbox"
            checked={optIn}
            onChange={(e) => setOptIn(e.target.checked)}
            className={checkboxClass}
          />
          <span className={checkboxLabelClass}>Yes, subscribe me to your newsletter. *</span>
        </label>
        <button
          type="submit"
          disabled={pending}
          className={buttonClass}
        >
          {pending ? 'Subscribing…' : 'Subscribe'}
        </button>
        {status === 'error' && (
          <p className={errorClass}>{errorText || home.newsletter.errorMsg}</p>
        )}
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-0 max-w-md mx-auto relative">
      <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
        <label>Leave this field empty<input type="text" name="website" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} /></label>
      </div>
      <label htmlFor={emailId} className="sr-only">Email address</label>
      <input
        id={emailId}
        type="email"
        value={email}
        onChange={(e) => { setEmail(e.target.value); setStatus('idle'); }}
        placeholder={home.newsletter.placeholder}
        required
        className={inputClass}
      />
      <button
        type="submit"
        disabled={pending}
        className={buttonClass}
      >
        {pending ? 'Signing up…' : home.newsletter.button}
      </button>
      {status === 'error' && (
        <p className={errorClass}>{errorText || home.newsletter.errorMsg}</p>
      )}
    </form>
  );
}
